import {
  BenefitHighlightsCard,
  ContentfulBenefitHighlightsTile
} from "#/components/home/benefits-modules/BenefitsHighlightsCard"
import CarrotRxHomeSidebarModule from "#/components/home/CarrotRxHomeSidebarModule"
import SprintsHomeSidebarModule from "#/components/home/SprintsHomeSidebarModule"
import { CarrotCardPreAuthModule } from "#/components/home/CarrotCardPreAuthModule"
import {
  AvailableFundsCardContainer,
  LimitedBenefitsEnrollmentExpiredCardContainer,
  LimitedBenefitsEnrollmentRequiredCardContainer,
  NeedsMemberUploadsCardContainer,
  UnlimitedBenefitEnrollmentCardContainer
} from "#/components/views/home/containers/sidebar"
import { useSidebarCards } from "#/components/views/home/hooks/use-sidebar-cards"
import { HelpCenterModule } from "#/components/views/molecules/HelpCenterModule"
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { Box, SurfaceColor } from "@carrotfertility/carotene-core"
import React from "react"
import { useFlags } from "launchdarkly-react-client-sdk"

const CARD_COLORS: SurfaceColor[] = ["green", "blue", "primary"]

interface SidebarCardsProps {
  isLoading: boolean
  showNeedsMemberUploads?: boolean
  showLimitedBenefitEnrollmentRequired?: boolean
  showLimitedBenefitEnrollmentExpired?: boolean
  showAvailableFunds?: boolean
  showUnlimitedBenefitEnrollment?: boolean
  showBenefitHighlights?: boolean
  showHelpCenter?: boolean
  showSprintsForFertility?: boolean
  showSprintsForMenopause?: boolean
  showCarrotRx?: boolean
  showCarrotCardPreAuth?: boolean
}

const SidebarCards = ({
  isLoading,
  showNeedsMemberUploads,
  showLimitedBenefitEnrollmentRequired,
  showLimitedBenefitEnrollmentExpired,
  showAvailableFunds,
  showUnlimitedBenefitEnrollment,
  showBenefitHighlights,
  showHelpCenter,
  showSprintsForFertility,
  showSprintsForMenopause,
  showCarrotRx,
  showCarrotCardPreAuth
}: SidebarCardsProps) => {
  const { contentfulHomePageBenefitHighlight } = useFlags()

  if (isLoading) {
    return <CenteredLoadingIndicator key={"centered-loading-indicator"} id={"loading-indicator-test"} />
  }

  const cardConfigs: { show: boolean; key: string; component: (color: SurfaceColor) => React.ReactNode }[] = [
    {
      show: showNeedsMemberUploads,
      key: "needs-member-uploads",
      component: (color) => <NeedsMemberUploadsCardContainer color={color} />
    },
    {
      show: showLimitedBenefitEnrollmentRequired,
      key: "enrollment-required",
      component: (color) => <LimitedBenefitsEnrollmentRequiredCardContainer color={color} />
    },
    {
      show: showLimitedBenefitEnrollmentExpired,
      key: "enrollment-expired",
      component: (color) => <LimitedBenefitsEnrollmentExpiredCardContainer color={color} />
    },
    {
      show: showUnlimitedBenefitEnrollment,
      key: "unlimited-enrollment",
      component: (color) => <UnlimitedBenefitEnrollmentCardContainer color={color} />
    },
    { show: showCarrotCardPreAuth, key: "carrot-card-preauth", component: () => <CarrotCardPreAuthModule /> },
    {
      show: !showCarrotCardPreAuth && showAvailableFunds,
      key: "available-funds",
      component: (color) => <AvailableFundsCardContainer color={color} />
    },
    {
      show: showBenefitHighlights && !contentfulHomePageBenefitHighlight,
      key: "benefit-highlights",
      component: (color) => <BenefitHighlightsCard color={color} />
    },
    {
      show: showBenefitHighlights && contentfulHomePageBenefitHighlight,
      key: "contentful-benefit-highlights",
      component: (color) => <ContentfulBenefitHighlightsTile color={color} />
    },
    { show: showHelpCenter, key: "help-center", component: (color) => <HelpCenterModule color={color} /> },
    {
      show: showSprintsForFertility,
      key: "sprints-fertility",
      component: (color) => <SprintsHomeSidebarModule color={color} variant="fertility" />
    },
    {
      show: showSprintsForMenopause,
      key: "sprints-menopause",
      component: (color) => <SprintsHomeSidebarModule color={color} variant="menopause" />
    },
    { show: showCarrotRx, key: "carrot-rx", component: (color) => <CarrotRxHomeSidebarModule color={color} /> }
  ]

  return (
    <>
      {cardConfigs
        .filter(({ show }) => show)
        .map(({ key, component }, index) => (
          <React.Fragment key={key}>{component(CARD_COLORS[index % CARD_COLORS.length])}</React.Fragment>
        ))}
    </>
  )
}

export const HomeSidebar = () => {
  const { data, isLoading } = useSidebarCards()
  return (
    <Box
      display="flex"
      flexWrap="wrap"
      flexDirection="row"
      gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
      sx={(theme) => ({
        [theme.breakpoints.down("lg")]: {
          "& > *": {
            flex: 1,
            blockSize: "auto",
            minInlineSize: "20rem",
            inlineSize: "calc(50% - 1.5rem)"
          }
        }
      })}
    >
      <SidebarCards
        isLoading={isLoading}
        showNeedsMemberUploads={data?.showNeedsMemberUploads}
        showLimitedBenefitEnrollmentRequired={data?.showLimitedBenefitEnrollmentRequired}
        showLimitedBenefitEnrollmentExpired={data?.showLimitedBenefitEnrollmentExpired}
        showAvailableFunds={data?.showAvailableFunds}
        showUnlimitedBenefitEnrollment={data?.showUnlimitedBenefitEnrollment}
        showBenefitHighlights={data?.showBenefitHighlights}
        showHelpCenter={data?.showHelpCenter}
        showSprintsForFertility={data?.showSprintsForFertility}
        showSprintsForMenopause={data?.showSprintsForMenopause}
        showCarrotRx={data?.showCarrotRx}
        showCarrotCardPreAuth={data?.showCarrotCardPreAuth}
      />
    </Box>
  )
}
